import { useCallback, useMemo, useState } from 'react';
import { representativeService } from 'shared/services/representativesService';
import { useDebounce, useDebouncedCallback } from 'use-debounce';
import useInfiniteScroll from 'shared/hooks/useInifiniteScrollAutoComplete';

const { getAgenciesWithPagination } = representativeService();

export const useAddRepresentativesGetAgencyOptions = (country: string) => {
  const [agencyInput, setAgencyInput] = useState<string>('');

  const handleAgencyInputChangeCB = useCallback((value: string) => {
    setAgencyInput(value);
  }, []);

  // Debounce agency search, to prevent instantaneous api calls
  const [agencySearch] = useDebounce(agencyInput, 700);

  // The Agency pagination functionality
  const {
    data: agencies,
    isLoading: isAgencyLoading,
    fetchNextPage,
    isError,
    hasNextPage,
    isFetchingNextPage: isFetchingAgencyNextPage,
  } = getAgenciesWithPagination({
    // The required queries
    country: country,
    search: agencySearch || '',
    first: 10,
  });

  // This extracts the pages, and only re-rendered the agency pages is changed
  const agency_options = useMemo(
    () =>
      agencies?.pages
        ?.map((page) => page.data?.agencies_connection?.nodes)
        .flat()
        ?.filter((agency) => agency?.full_name || agency?.name) || [],
    [agencies?.pages],
  );

  /* 
    The onscroll observer to see if the list container is on the bottom, this function is connected to the autoComplete listBoxProps
    in Line 84, in AddRepresentativesFormListAgencyTypeField.tsx 
  */
  const { handleScroll } = useInfiniteScroll({
    onPageChange: fetchNextPage,
    hasNextPage,
  });

  // A debounce this prevents unnecessary calls when scrolling down
  const handleListScroll = useDebouncedCallback(handleScroll, 500);

  return {
    agency_options,
    handleAgencyInputChangeCB,
    isError,
    isAgencyLoading,
    handleListScroll,
    isFetchingAgencyNextPage,
  };
};
